import EditIcon from '@mui/icons-material/EditOutlined';
import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import React from 'react';
import { Bookmark } from '../../../functions/src/types';
import { useDataContext } from '../context/DataContext';
import { useLocation, useNavigate, useParams } from 'react-router';
import { extractUrlFromText } from '../Utils';

interface ModifyBookmarkDialogProps {
  open: boolean;
}

export const ModifyBookmarkDialog: React.FC<ModifyBookmarkDialogProps> = (
  { open }
) => {

  const navigate = useNavigate();
  const location = useLocation();
  const { saveModifiedBookmark, allBookmarks, allTags } = useDataContext();
  const { fid } = useParams();
  const [bookmarkUrl, setBookmarkUrl] = React.useState('');
  const [bookmarkTitle, setBookmarkTitle] = React.useState('');
  const [bookmarkSummary, setBookmarkSummary] = React.useState('');
  const [bookmarkTags, setBookmarkTags] = React.useState<string[]>([]);
  const [bookmarkToModify, setBookmarkToModify] = React.useState<Bookmark | null>(null);
  const pendingTagInputRef = React.useRef('');
  const trimmedBookmarkUrl = bookmarkUrl.trim();
  const cleanedBookmarkUrl = trimmedBookmarkUrl ? extractUrlFromText(trimmedBookmarkUrl) : undefined;
  const hasUrlInput = trimmedBookmarkUrl.length > 0;
  const urlError = hasUrlInput && !cleanedBookmarkUrl ? 'Enter a valid URL, for example https://example.com or example.com' : '';
  const urlHelperText = urlError || 'Enter a full URL like https://example.com, or just a domain like example.com';

  React.useEffect(() => {
    if (allBookmarks && fid && (!bookmarkToModify || bookmarkToModify.fid !== fid)) {
      const searchedBookmark = allBookmarks?.find(bookmark => bookmark.fid === fid);
      if (searchedBookmark) {
        setBookmarkToModify(searchedBookmark);
        setBookmarkUrl(searchedBookmark.url);
        setBookmarkTitle(searchedBookmark.title);
        setBookmarkSummary(searchedBookmark.summary);
        setBookmarkTags(searchedBookmark.tags || []);
        pendingTagInputRef.current = '';
      }
    }
  }, [allBookmarks, bookmarkToModify, fid]);

  const closeDialog = React.useCallback(() => {
    navigate({
      pathname: '/',
      search: location.search,
    });
  }, [location.search, navigate]);

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth>
      <Stack spacing={2}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EditIcon color="secondary" /> Edit bookmark</Box></DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label='Name' variant='outlined' size="small"
              fullWidth
              value={bookmarkTitle}
              onChange={(event) => setBookmarkTitle(event.target.value)}
            />
            <TextField label='Description' variant='outlined' size="small"
              fullWidth
              multiline
              value={bookmarkSummary}
              onChange={(event) => setBookmarkSummary(event.target.value)}
            />
            <TextField
              label='Bookmark URL'
              variant='outlined'
              size="small"
              type="url"
              placeholder='https://example.com'
              fullWidth
              value={bookmarkUrl}
              onChange={(event) => {
                setBookmarkUrl(event.target.value);
              }}
              onBlur={() => {
                if (cleanedBookmarkUrl && cleanedBookmarkUrl !== bookmarkUrl) {
                  setBookmarkUrl(cleanedBookmarkUrl);
                }
              }}
              error={!!urlError}
              helperText={urlHelperText}
            />
            <Autocomplete
              multiple
              freeSolo
              autoSelect
              options={allTags.filter(t => !bookmarkTags.includes(t))}
              value={bookmarkTags}
              onChange={(_event, newValue) => {
                setBookmarkTags(newValue as string[]);
                pendingTagInputRef.current = '';
              }}
              onInputChange={(_event, newInputValue) => {
                pendingTagInputRef.current = newInputValue;
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    color="secondary"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" variant="outlined" size="small" fullWidth />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant='outlined'>Cancel</Button>
          <Button
            variant='contained' color='secondary'
            disabled={!hasUrlInput || !cleanedBookmarkUrl || !bookmarkTitle.trim()}
            onClick={
              () => {
                if (bookmarkToModify) {
                  if (!cleanedBookmarkUrl) {
                    return;
                  }

                  const finalTags = [...bookmarkTags];
                  const pending = pendingTagInputRef.current.trim();
                  if (pending && !finalTags.includes(pending)) {
                    finalTags.push(pending);
                  }
                  const updatedBookmark = {
                    url: cleanedBookmarkUrl,
                    title: bookmarkTitle,
                    summary: bookmarkSummary,
                    addedOn: bookmarkToModify.addedOn,
                    fid: bookmarkToModify.fid,
                    tags: finalTags,
                  } as Bookmark;
                  saveModifiedBookmark(updatedBookmark);
                }
                closeDialog();
              }
            }>Save</Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
